import { Box, Button, Divider, Link, Stack, Typography } from '@mui/material';
import { cloneDeep, get, set } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import LoadingBackdrop from 'src/components/ui-component/loading/LoadingBackdrop';
import MuiSnackbar from 'src/components/ui-component/mui-snackbar/MuiSnackbar';
import { useAuthentication } from 'src/context/AuthContext';
import { RegisterUserAndOrganisation, SendForgotPasswordMail } from 'src/lib/api/AuthUser';
import GetRecord from 'src/lib/api/GetRecord';
import PutRecord from 'src/lib/api/PutRecord';
import { APIS } from 'src/lib/constants/apis';
import { BENUTZERDATEN_PORTAL_ROLLE, CUSTOM_PATH, NOT_FOUND_ERROR_MESSAGE } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { isObjectEmpty, isTrue, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import theme from '../../../../../../../../assets/theme/theme';
import { hasAdminRole } from '../../../NutzerVerwaltung';
import EditPersonAndUserValidator from './EditPersonAndUserValidation';
import EditPersonValidator from './EditPersonValidator';

// import schema
import PersonSchema from 'src/lib/paths/person_FULL_Paths.json';
import UserSchema from 'src/lib/paths/user_Paths.json';
const P = PersonSchema.attributes;
const UP = UserSchema.attributes;

const EditPerson: FC<{ handleToggleActiveShowCase: () => void; personId: number }> = ({ handleToggleActiveShowCase, personId }) => {
	const { authenticatedData } = useAuthentication();

	const [loading, setLoading] = useState(false);
	const [isFormEdited, setIsFormEdited] = useState(false);
	const [formValues, setFormValues] = useState({});
	const [formValuesError, setFormValuesError] = useState({});
	const [formErrorMessage, setFormErrorMessage] = useState('');
	const [formSuccessMessage, setFormSuccessMessage] = useState('');

	const hasUserData = useMemo(() => {
		return isObjectEmpty(get(formValues, P.User.path, {})) ? false : true;
	}, [formValues]);
	const isConfirmed = get(formValues, `${P.User.path}.attributes.${UP.confirmed.path}`);

	const setFormValuesFunction = (formValues: any) => {
		const isAdminKeyExists = formValues.hasOwnProperty('isAdmin');
		const updatedFormValues =
			isAdminKeyExists ? formValues : { ...formValues, isAdmin: hasAdminRole(get(formValues, P.Portal_Benutzerdaten_Portal_Rolle.path)) };
		setFormValues(updatedFormValues);
	};

	const handleUpdateFormValues = (key: string, value: any) => {
		let formValueCopy = cloneDeep(formValues);
		set(formValueCopy, key, value);

		if (key === CUSTOM_PATH.isAdmin) {
			set(formValueCopy, P.Portal_Benutzerdaten_Portal_Rolle.path, value ? BENUTZERDATEN_PORTAL_ROLLE.Admin : BENUTZERDATEN_PORTAL_ROLLE.Standard);
		}

		let errorObject = cloneDeep(formValuesError);
		delete errorObject[key];
		setIsFormEdited(true);
		setFormValuesFunction(formValueCopy);
		setFormValuesError(errorObject);
	};

	const validateFormValues = () => {
		let errorObject = {};
		let formValueCopy = cloneDeep(formValues);

		if (!isTrue(get(formValueCopy, 'attributes.addUser'))) {
			let errs = EditPersonValidator.validate(formValueCopy);
			for (let err of errs) {
				set(errorObject, err.path, get(err, 'message'));
			}
			setFormValuesError(errorObject);
			return Object.keys(errorObject).length === 0;
		} else {
			let errs = EditPersonAndUserValidator.validate(formValueCopy);
			for (let err of errs) {
				set(errorObject, err.path, get(err, 'message'));
			}
			setFormValuesError(errorObject);
			return Object.keys(errorObject).length === 0;
		}
	};

	// const handleFormSubmit = async () => {
	// 	if (isObjectEmpty(get(formValues, P.User.path, {}))) {
	// 		await handleUpdatePersonAndAddUser();
	// 	}
	// };

	const handleFormSubmit = async () => {
		if (validateFormValues()) {
			setLoading(true);
			try {
				let personRes = await PutRecord(
					APIS.Person,
					personId,
					{
						data: {
							...get(formValues, 'attributes'),
							Portal_Benutzerdaten_Portal_Rolle:
								get(formValues, CUSTOM_PATH.isAdmin, false) ? BENUTZERDATEN_PORTAL_ROLLE.Admin : BENUTZERDATEN_PORTAL_ROLLE.Standard,
						},
					},
					['User', 'Organisation'],
				);
				let userRes: any;
				if (personRes && isObjectEmpty(get(formValues, P.User.path, {})) && isTrue(get(formValues, 'attributes.addUser'))) {
					if (isTrue(get(formValues, 'attributes.addUser'))) {
						const userData = {
							...formValues,
							email: get(formValues, P.Kontaktdaten_E_Mail.path),
						};
						userRes = await RegisterUserAndOrganisation({}, userData, {}, true, personId, get(authenticatedData, 'organisationId', -1));
					}
				}
				if (personRes && userRes) {
					setFormSuccessMessage('Person wurde aktualisiert und Benutzerdaten wurde erfolgreich angelegt');
				} else if (personRes) {
					setFormSuccessMessage('Persondaten wurde erfolgreich aktualisiert');
				}
				// setFormValues(removeNullDeep(removeDataKeys(get(personRes, 'data', {}))));
				setIsFormEdited(false);
			} catch (err: any) {
				console.error('ERROR: Updating Person ', err);
				setFormErrorMessage(NOT_FOUND_ERROR_MESSAGE);
			} finally {
				setLoading(false);
			}
		}
	};
	// const handleUpdateUserAdminRolle = async (key: string, value: any) => {
	// 	let formValueCopy = cloneDeep(formValues);
	// 	set(formValueCopy, key, value);
	// 	setFormValuesFunction(formValueCopy);
	// 	try {
	// 		setLoading(true);
	// 		const userData = {
	// 			Portal_Benutzerdaten_Portal_Rolle: value ? BENUTZERDATEN_PORTAL_ROLLE.Admin : BENUTZERDATEN_PORTAL_ROLLE.Standard,
	// 		};
	// 		let userRes = await PutRecord(APIS.Person, personId, { data: userData });
	// 		if (userRes) {
	// 			setFormSuccessMessage('Admin Rechte wurde aktualisiert');
	// 		}
	// 	} catch (err: any) {
	// 		console.error('ERROR: Updating user ', err);
	// 	} finally {
	// 		setLoading(false);
	// 	}
	// };

	const handleUpdateUserConfirmed = async (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		let formValueCopy = cloneDeep(formValues);
		set(formValueCopy, `${P.User.path}.attributes.${UP.confirmed.path}`, !isConfirmed);
		setFormValuesFunction(formValueCopy);
		try {
			setLoading(true);
			const userData = {
				confirmed: !isConfirmed,
			};
			let userRes = await PutRecord(APIS.user, get(formValues, `${P.User.path}.id`), userData);
			if (userRes) {
				setFormSuccessMessage('Benutzeraktivierung wurde aktualisiert');
			}
		} catch (err: any) {
			console.error('ERROR: Updating user ', err);
		} finally {
			setLoading(false);
		}
	};

	const handleForgotPassword = async (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		try {
			setLoading(true);
			const userData = {
				username: get(formValues, `${P.User.path}.attributes.${UP.username.path}`),
			};
			let userRes = await SendForgotPasswordMail(userData);
			if (userRes) {
				setFormSuccessMessage('Ein Link zum Zurücksetzen des Passworts wurde per E-Mail versendet.');
			}
		} catch (err: any) {
			console.error('ERROR: Updating user ', err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const fetch = async () => {
			try {
				setLoading(true);
				let personRes = await GetRecord(APIS.Person, personId, ['User', 'Organisation']);
				setFormValuesFunction(removeNullDeep(removeDataKeys(get(personRes, 'data', {}))));
			} catch (err: any) {
				console.error('ERROR: Fetching Person and User ', err);
				setFormErrorMessage(NOT_FOUND_ERROR_MESSAGE);
			} finally {
				setLoading(false);
			}
		};

		if (personId) fetch();
	}, [personId]);

	return (
		<>
			<LoadingBackdrop open={loading} />
			<MuiSnackbar
				showSnackbar={Boolean(formErrorMessage)}
				closeSnackbar={() => setFormErrorMessage('')}
				alertMessage={formErrorMessage}
				error={true}
			/>
			<MuiSnackbar showSnackbar={Boolean(formSuccessMessage)} closeSnackbar={() => setFormSuccessMessage('')} alertMessage={formSuccessMessage} />
			<Box sx={{ mt: '0.625rem' }}>
				<Typography variant='h3' component={'h1'}>
					Profil bearbeiten
				</Typography>
				<CreateForm
					formValues={formValues}
					formStruc={formStrucPerson(hasUserData)}
					updateFormValuesFunc={handleUpdateFormValues}
					formErrors={formValuesError}
					api={APIS.Person}
				/>

				<Box sx={{ mt: '2rem' }}>
					{hasUserData ?
						<>
							<Typography variant='h3' component={'h2'}>
								Konto-Verwaltung
							</Typography>
							<Box sx={{ border: `1px solid` + theme.palette.border.primary, padding: '1rem' }}>
								<CreateForm
									formValues={formValues}
									formStruc={formStrucEditUser()}
									updateFormValuesFunc={handleUpdateFormValues}
									formErrors={formValuesError}
									api={APIS.Person}
								/>
								<Box sx={{ marginTop: '16px' }}>
									<Link
										href='#'
										onClick={handleForgotPassword}
										underline='hover'
										color='primary'
										// fontWeight='500'
										sx={{ my: 2, cursor: 'pointer' }}
									>
										Link zum Passwort-zurücksetzen als Mail senden
									</Link>
								</Box>
								<CreateForm
									formValues={formValues}
									formStruc={formStrucEditUserAdminRechte()}
									updateFormValuesFunc={handleUpdateFormValues}
									formErrors={formValuesError}
									api={APIS.Person}
								/>
								<Divider variant='middle' sx={{ my: 2 }} />
								<Box display='flex' justifyContent='space-between'>
									<Typography display='inline'>
										Das Konto für dieses Profil ist{' '}
										<Typography component='span' sx={{ fontWeight: 'bold', color: isConfirmed ? 'green' : 'red' }}>
											{' '}
											{isConfirmed ? 'aktiv' : 'inaktiv'}{' '}
										</Typography>
									</Typography>
									<Link
										href='#'
										onClick={handleUpdateUserConfirmed}
										underline='always'
										sx={{ color: 'black', cursor: 'pointer', textDecorationColor: 'black' }}
									>
										{isConfirmed ? 'Profil deaktivieren' : 'Profil aktivieren'}
									</Link>
								</Box>
							</Box>
						</>
					:	<>
							{isTrue(get(formValues, 'attributes.addUser')) && (
								<>
									<Typography variant='h3' component={'h2'}>
										Konto-Verwaltung
									</Typography>
									<Box sx={{ border: `1px solid` + theme.palette.border.primary, padding: '1rem' }}>
										<CreateForm
											formValues={formValues}
											formStruc={formStrucAddUser()}
											updateFormValuesFunc={handleUpdateFormValues}
											formErrors={formValuesError}
											api={APIS.Person}
										/>
									</Box>
								</>
							)}
						</>
					}
				</Box>

				<Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-end'} sx={{ mt: '2rem' }}>
					<Button variant={'outlined'} onClick={() => handleToggleActiveShowCase()}>
						Abbrechen
					</Button>
					<Button onClick={handleFormSubmit} disabled={!isFormEdited}>
						Speichern
					</Button>
				</Stack>
			</Box>
		</>
	);
};

export default EditPerson;

/**
 * HA-2700: isAdmin feld soll mit dem Button speichern und nicht direkt über Checkbox. Add User wird redundant und kann entfernt werden, wenn nicht mehr genutzt wird später. Beim Einladen wird jetzt IMMER ein User erstellt.
 */
const formStrucPerson = (hasUserData: boolean) => {
	let struc: FormStrucItem[] = [
		{
			fields: [P.Personenbezogene_Daten_Anrede, P.Personenbezogene_Daten_Titel],
			userProps: { direction: 'column', columnToDivide: 2 },
		},
		{
			fields: [P.Personenbezogene_Daten_Vorname, P.Personenbezogene_Daten_Nachname],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [P.Kontaktdaten_E_Mail, P.Kontaktdaten_Telefon],
			userProps: { columnToDivide: 2 },
		},
	];

	if (!hasUserData) {
		struc.push({
			fields: [
				{
					path: 'attributes.addUser',
					label: 'Aktive Nutzung: Benötigt dieser Benutzer*in Login-Daten?',
					type: 'boolean',
				},
			],
			userProps: { columnToDivide: 1 },
		});
	}

	return struc;
};
const formStrucEditUser = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					label: 'E-mail',
					path: `${P.User.path}.attributes.${UP.email.path}`,
					type: 'string',
					disabled: true,
				},
				{
					label: 'Benutzername',
					path: `${P.User.path}.attributes.${UP.username.path}`,
					type: 'string',
					disabled: true,
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];
	return struc;
};
const formStrucEditUserAdminRechte = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: CUSTOM_PATH.isAdmin,
					type: 'boolean',
					default: true,
					label: 'Diesem Profil Administrative Rechte zuweisen',
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucAddUser = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [{ ...P.Kontaktdaten_E_Mail, disabled: true }, UP.username],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [UP.password, UP.repeatPassword],
			userProps: { columnToDivide: 2 },
		},
		{
			fields: [
				{
					path: CUSTOM_PATH.isAdmin,
					type: 'boolean',
					default: true,
					label: 'Diesem Profil Administrative Rechte zuweisen',
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};
